import React, { useState, useEffect, useRef } from "react";
import { fetchProjects } from "../api";
import "./HomePage.css";

export default function HomePage() {
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [aboutMode, setAboutMode] = useState(false);
  const visibleProjects = projects.filter(proj => proj.show);

  // Overlay/fade logic
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [overlayFading, setOverlayFading] = useState(false);
  const overlayTimeoutRef = useRef();

  // For short project detection
  const [isShortProject, setIsShortProject] = useState(false);
  const projectContentRef = useRef(null);

  // SCROLL CONTROL: use ref for scrollable container
  const mainScrollRef = useRef();

  // Track when images/videos have loaded for "project" view
  const [mediaLoadedCount, setMediaLoadedCount] = useState(0);
  const [mediaExpectedCount, setMediaExpectedCount] = useState(0);

  // -------- UNIVERSAL SCROLL RESETTER FOR .main-scroll --------
  const resetScroll = () => {
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTop = 0;
    }
  };

  // -------- FETCH PROJECTS --------
  useEffect(() => {
    async function load() {
      try {
        const data = await fetchProjects();
        setProjects(data);
      } catch (err) {
        setError(err);
      } finally {
        setTimeout(() => setOverlayFading(true), 300);
        setTimeout(() => setOverlayVisible(false), 1100);
      }
    }
    load();
    setOverlayVisible(true);
    setOverlayFading(false);
  }, []);

  // -------- OVERLAY ON NAVIGATION --------
  const triggerOverlay = () => {
    setOverlayVisible(true);
    setOverlayFading(false);
    clearTimeout(overlayTimeoutRef.current);
    overlayTimeoutRef.current = setTimeout(() => setOverlayFading(true), 200);
    overlayTimeoutRef.current = setTimeout(() => setOverlayVisible(false), 1000);
  };

  // -------- NAV HANDLER --------
  const handleNav = ({ toAbout = false, toProject = null }) => {
    if (
      (aboutMode && toAbout) ||
      (activeProject && toProject && activeProject.id === toProject.id) ||
      (!aboutMode && !activeProject && !toAbout && !toProject)
    ) {
      return;
    }
    triggerOverlay();
    setTimeout(() => {
      setAboutMode(toAbout);
      setActiveProject(toProject);
      setZoomedImage(null);
      setMediaLoadedCount(0);
      // After render
      setTimeout(() => resetScroll(), 0);
    }, 80);
  };

  // -------- SHORT PROJECT BUTTON LOGIC --------
  useEffect(() => {
    if (!activeProject) return setIsShortProject(false);
    const check = () => {
      if (!projectContentRef.current) return;
      const contentHeight = projectContentRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      setIsShortProject(contentHeight + 40 < windowHeight);
    };
    setTimeout(check, 100);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [activeProject, projects, mediaLoadedCount]);

  // -------- RESET SCROLL AFTER OVERLAY FADES OUT --------
  useEffect(() => {
    if (!overlayVisible) {
      resetScroll();
    }
  }, [overlayVisible]);

  // -------- RESET SCROLL AFTER MEDIA LOADS (PROJECT PAGE) --------
  useEffect(() => {
    if (activeProject && mediaExpectedCount > 0 && mediaLoadedCount === mediaExpectedCount) {
      resetScroll();
    }
  }, [activeProject, mediaLoadedCount, mediaExpectedCount]);

  // -------- SET MEDIA EXPECTED COUNT ON PROJECT CHANGE --------
  useEffect(() => {
    if (!activeProject) {
      setMediaExpectedCount(0);
      setMediaLoadedCount(0);
      return;
    }
    let count = 0;
    if (activeProject.gallery) count += activeProject.gallery.length;
    if (activeProject.video1?.url) count += 1;
    if (activeProject.video2?.url) count += 1;
    setMediaExpectedCount(count);
    setMediaLoadedCount(0);
  }, [activeProject]);

  const handleMediaLoad = () => setMediaLoadedCount(c => c + 1);
  const handleImageClick = (url) => setZoomedImage(url);

  const renderVideo = (url) => {
    if (!url) return null;
    const isYouTube = url.includes("youtube.com") || url.includes("youtu.be");
    if (isYouTube) {
      let videoId = url.includes("youtu.be/")
        ? url.split("youtu.be/")[1].split("?")[0]
        : new URL(url).searchParams.get("v");
      return (
        <div className="project-video-wrapper">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ width: "100%", border: "none", aspectRatio: "16/9" }}
            onLoad={handleMediaLoad}
          />
        </div>
      );
    }
    return (
      <div className="project-video-wrapper">
        <video src={url} controls className="project-video" onCanPlayThrough={handleMediaLoad} />
      </div>
    );
  };

  // --- Nav Bar ---
  const NavBar = (
    <div className="centered-nav">
      <div
        className={`nav-link ${!aboutMode && !activeProject ? "active" : ""}`}
        style={{ opacity: 1, color: "#000" }}
        onClick={() => handleNav({ toAbout: false, toProject: null })}
      >
        Work
      </div>
      <a
        className="nav-link"
        style={{ opacity: 1, color: "#000" }}
        href="mailto:dubois.leo@hotmail.fr"
        onClick={e => { e.preventDefault(); window.location.href = "mailto:dubois.leo@hotmail.fr"; }}
      >
        Contact
      </a>
      <div
        className={`nav-link ${aboutMode ? "active" : ""}`}
        style={{ opacity: 1, color: "#000" }}
        onClick={() => handleNav({ toAbout: true, toProject: null })}
      >
        About
      </div>
    </div>
  );

  // --- Overlay (always rendered, fades via class) ---
  const LoadingOverlay = overlayVisible && (
    <div className={`loading-overlay${overlayFading ? " hide" : ""}`}>
      {/* <div className="loading-spinner" /> */}
    </div>
  );

  if (error || (!projects.length && !overlayVisible)) {
    return (
      <>
        {NavBar}
        {LoadingOverlay}
        <div style={{ padding: 40 }}>
          <p>Error loading projects.</p>
        </div>
      </>
    );
  }

  return (
    <>
      {NavBar}
      {LoadingOverlay}
      <div className="main-scroll" ref={mainScrollRef}>
        {/* About Page */}
        {aboutMode ? (
          <div className="about-centered">
            <div className="sidebar-about">
              Imagery, AI, and design. Based in Paris.
            </div>
          </div>
        ) : activeProject ? (
          <div className="project-detail-container">
            <div className="project-content-centered" ref={projectContentRef}>
              <h2 className="project-title">{activeProject.title}</h2>
              <p className="project-description">{activeProject.description}</p>
              {activeProject.credits && (
                <p className="project-credits">{activeProject.credits}</p>
              )}
              {renderVideo(activeProject.video1?.url)}
              {renderVideo(activeProject.video2?.url)}
              {activeProject.two ? (
                <div className="gallery-grid">
                  {activeProject.gallery.map((item) => (
                    <div key={item.id} className="gallery-grid-item">
                      <img
                        src={item.url}
                        alt=""
                        className="project-image"
                        onClick={() => handleImageClick(item.url)}
                        style={{ cursor: "pointer" }}
                        onLoad={handleMediaLoad}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="gallery-grid single-col">
                  {activeProject.gallery.map((item) => (
                    <div key={item.id} className="gallery-grid-item">
                      <img
                        src={item.url}
                        alt=""
                        className="project-image"
                        onClick={() => handleImageClick(item.url)}
                        style={{ cursor: "pointer" }}
                        onLoad={handleMediaLoad}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            {zoomedImage && (
              <div className="zoom-overlay" onClick={() => setZoomedImage(null)}>
                <img src={zoomedImage} className="zoomed-image" alt="" />
              </div>
            )}
            {/* NEXT PROJECT BUTTON */}
            <div
              className={
                "desktop-next-project" + (isShortProject ? " desktop-next-project-absolute" : "")
              }
              onClick={() => {
                const idx = visibleProjects.findIndex(p => p.id === activeProject.id);
                const nextIdx = (idx + 1) % visibleProjects.length;
                triggerOverlay();
                setTimeout(() => {
                  setActiveProject(visibleProjects[nextIdx]);
                  setMediaLoadedCount(0);
                  setTimeout(() => resetScroll(), 0);
                }, 80);
              }}
            >
              NEXT PROJECT
            </div>
          </div>
        ) : (
          <div className="projects-grid">
            {visibleProjects.map((proj) => (
              <div
                className="project-grid-item"
                key={proj.id}
                onClick={() => {
                  handleNav({ toAbout: false, toProject: proj });
                }}
              >
                <img
                  src={proj.thumbnail?.url || proj.gallery?.[0]?.url}
                  alt={proj.title}
                  className="project-grid-image"
                  loading="lazy"
                  onLoad={resetScroll}
                />
                <div className="project-grid-title">{proj.title}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
